import { KikGenericResponse } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { setServiceSetting } from "@springtree/eva-sdk-core-service";
import { GetServerSidePropsContext, GetStaticPropsContext } from "next";
import { AbstractIntlMessages } from "next-intl";

import { Breadcrumb } from "~/components/product/product-breadcrumbs";
import Contentful from "~/services/contentful";
import Eva from "~/services/eva";
import EvaAdmin from "~/services/eva-admin";
import { HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { ApiConstants } from "~/shared/api/api-constants.class";
import { ApiUtils } from "~/shared/api/api-utils.class";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";

import CommonUtils from "./common-utils";
import contentfulUtils from "./contentful-utils";
import { assertEditorialType, isActiveEntry, isSameEntry } from "./editorial-utils";
import evaUtils from "./eva-utils";
import powerReviewUtils from "./power-reviews-utils";
import productUtils from "./product-utils";

type GetPropsContext = GetServerSidePropsContext | GetStaticPropsContext;

type GetServerSideDataOptions = {
  isCheckout?: boolean;
};

const ssrUtils = {
  async getEvaConfiguration(context: GetPropsContext) {
    const evaAdmin = new EvaAdmin({ locale: context.locale });
    const detailedOrg = await evaAdmin.getOrganizationUnitForLocale(context.locale!);
    const organizationUnitId = detailedOrg.ID;
    const eva = new Eva({ locale: context.locale, organizationUnitId });
    const configuration = await eva.getApplicationConfiguration();

    setServiceSetting("requestedOrganizationUnitID", organizationUnitId);

    return { organizationUnitId, organizationUnitBackendId: detailedOrg.BackendID, configuration };
  },

  /**
   * Retrieves editorial defined categories information for the given categories
   * @param contentful Contentful instance
   * @param categoryIds Categories hierarchy codes
   * @returns Info about given categories
   */
  async extractHierarchicalCategoryData(contentful: Contentful, categoryIds: string[]): Promise<Breadcrumb[]> {
    let categoryBreadcrumbs: Breadcrumb[] = [];
    const categoryBindings = await contentful.getCategoryBindingsFromIds(categoryIds);

    for (const categoryId of categoryIds) {
      const categoryBinding = categoryBindings.find((entry) => {
        // @ts-expect-error - Untyped Entry
        return entry.fields.categoryId === categoryId;
      });

      if (categoryBinding) {
        assertEditorialType<Editorials.CategoryBinding>(categoryBinding, "categoryBinding");
        categoryBreadcrumbs.push({
          text: categoryBinding.fields.name,
          url: categoryBinding.fields.slug ? `/c/${categoryBinding.fields.slug}/` : null,
        });
      }
    }
    return categoryBreadcrumbs;
  },

  async getServerSideData(
    context: GetPropsContext,
    preview = false,
    options: GetServerSideDataOptions = {},
    /**
     * If any previous network request fetched the configuration, this function can skip loading.
     */
    eva?: Awaited<ReturnType<typeof this.getEvaConfiguration>>
  ) {
    eva ??= await this.getEvaConfiguration(context);
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });

    // In preview mode the MAPI admin endpoints are used: no Cache-Control (CDN cache bypass), Basic Auth (mapiadmin).
    const mapiAdminUser = process.env.MAPI_ADMIN_USER;
    const mapiAdminPassword = process.env.MAPI_ADMIN_PASSWORD;
    const useMapiAdmin = preview && !!mapiAdminUser && !!mapiAdminPassword;
    const mapiAdminHeaders = useMapiAdmin
      ? { headers: { Authorization: ApiUtils.credentialsToBasicAuth(mapiAdminUser!, mapiAdminPassword!) } }
      : {};

    const translationsUrl = ApiUtils.interpolateParams(
      useMapiAdmin
        ? ApiConstants.endpointsMapiV1.ecoContentfulTranslationsAdmin
        : ApiConstants.endpointsMapiV1.ecoContentfulTranslations,
      { locale: context.locale || "default" }
    );

    const messagesPromise = HttpService.$http<KikGenericResponse<AbstractIntlMessages>>({
      url: translationsUrl,
      ...mapiAdminHeaders,
    })
      .then((response) => response.data ?? {})
      .catch((error): AbstractIntlMessages => {
        Logger.instance.error(`Translations: failed to fetch ${translationsUrl}`, error);
        return {};
      });

    // Country dialog: normally loaded client-side from the public (CDN cached) endpoint.
    // In preview mode it is loaded here from the admin endpoint and passed down through the props.
    const countryDialogPromise: Promise<Editorials.CountriesModel | null> = useMapiAdmin
      ? HttpService.$http<KikGenericResponse<Editorials.CountriesModel>>({
          url: ApiConstants.endpointsMapiV1.ecoContentfulCountryDialogAdmin,
          ...mapiAdminHeaders,
        })
          .then((response) => response.data ?? null)
          .catch((error) => {
            Logger.instance.error("Country dialog: failed to fetch from the MAPI admin endpoint", error);
            return null;
          })
      : Promise.resolve(null);

    // Navigation
    const navigationPromise = contentful.getEntriesByContentType("navigation", 1, true).then((c) => {
      return c.items[0];
    });

    // Search
    const searchPromise = contentful.getSearchPage().then((entry) => {
      return entry.fields as Editorials.SearchPage;
    });

    // Footer
    const footerPromise = contentful.getEntriesByContentType("footer", 1, true).then((c) => c.items[0]);

    //content utils
    const contentUtilsPromise = contentful.getEntriesByContentType("contentUtils", 1).then((c) => c.items[0]);

    const [messages, navigation, search, footer, contentUtils, countryDialog] = await Promise.all([
      messagesPromise,
      navigationPromise,
      searchPromise,
      footerPromise,
      contentUtilsPromise,
      countryDialogPromise,
    ]);

    assertEditorialType<Editorials.ContentUtils>(contentUtils, "contentUtils");
    const prefooter = options.isCheckout ? contentUtils.fields.checkoutPrefooter : contentUtils.fields.defaultPrefooter;

    const { organizationUnitId, configuration, organizationUnitBackendId } = eva;

    let timeZones: string[] = [];

    if (context.locale?.includes("-")) {
      const [lang, country] = context.locale.split("-");
      const locale = new Intl.Locale(`${lang}-${country.toUpperCase()}`);

      // @ts-expect-error - The ES2020 lib for TypeScript does not have this.
      if (Array.isArray(locale.timeZones)) {
        // @ts-expect-error - The ES2020 lib for TypeScript does not have this.
        timeZones = locale.timeZones;
        // @ts-expect-error - The ES2020 lib for TypeScript does not have this.
      } else if (typeof locale.getTimeZones === "function") {
        // @ts-expect-error - The ES2020 lib for TypeScript does not have this.
        timeZones = locale.getTimeZones();
      }
    }

    return {
      organizationUnitId,
      organizationUnitBackendId,
      contentUtils,
      configuration,
      messages,
      navigation,
      prefooter,
      footer,
      search,
      timeZones,
      draftMode: context.draftMode || false,
      countryDialog,
    };
  },

  async getServerSideEditorialPageData(
    context: GetPropsContext,
    preview = false,
    slug = context.params?.slug,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    const contentfulSlug = Array.isArray(slug) ? slug.join("/") : slug!;

    // Editorial page data
    const pagePromise = contentful.getEditorialPageBySlug(contentfulSlug);

    // Slugs of all locales
    const editorialPageSlugsPromise = contentful.getSlugsOfAllLocales("editorialPage", contentfulSlug);

    const [data, page, editorialPageSlugs] = await Promise.all([dataPromise, pagePromise, editorialPageSlugsPromise]);

    if (page) {
      assertEditorialType<Editorials.EditorialPage>(page, "editorialPage");
      return {
        ...data,
        slugs: editorialPageSlugs,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideEditorialPreviewPageData(
    context: GetPropsContext,
    slug = context.params?.slug,
    options: GetServerSideDataOptions = {}
  ) {
    return this.getServerSideEditorialPageData(context, true, slug, options);
  },

  async getServerSideLoginPageData(
    context: GetServerSidePropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getLoginPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.SpecialPage>(page, "specialPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideRequestReturneData(
    context: GetServerSidePropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getRequestReturnPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.SpecialPage>(page, "specialPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideTrackYourOrderData(
    context: GetServerSidePropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getTrackYourOrderPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.SpecialPage>(page, "specialPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideThankyouPageData(
    context: GetServerSidePropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getThankyouPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.SpecialPage>(page, "specialPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  // Generic getter for all the pages which only show a message => we can use a generic component and pass the message content (text or html)
  async getServerSideGenericPageData(
    // we can create a type here for available slugs + contentTypes (one contentType can have multiple slugs such as passwordResetConfirmationPage)
    slug:
      | string
      | "password-reset-request-sent"
      | "password-reset"
      | "password-reset-request"
      | "password-reset-complete",
    context: GetServerSidePropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getMessagePage(slug);

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.SpecialPage>(page, "specialPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideProductListingPageData(
    context: GetPropsContext,
    preview = false,
    slug = context.params?.slug,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const formattedLocale = contentfulUtils.mapLocale(context.locale);
    const contentful = new Contentful({ preview, locale: formattedLocale });
    const evaConfiguration = await this.getEvaConfiguration(context);
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getProductListingPageBySlug(slug as string);

    // Slugs of all locales
    const categoryBindingSlugsPromise = contentful.getSlugsOfAllLocales("categoryBinding", slug as string);

    const [data, page, categoryBindingSlugs] = await Promise.all([
      dataPromise,
      pagePromise,
      categoryBindingSlugsPromise,
    ]);

    if (page) {
      assertEditorialType<Editorials.ProductListingPage>(page, "productListingPage");
      const categoryPath = page.fields.category.fields.categoryId;
      let categorySegments = categoryPath?.split("_") ?? [];
      let slugSegments = page.fields.category.fields.slug?.split("/") ?? [];
      let hierarchicalCategoryData = [];
      let reviews = null;

      if (categorySegments.length > 0) {
        const categoryIds = categorySegments.map((_, idx, array) => array.slice(0, idx).join("_"));
        hierarchicalCategoryData = await this.extractHierarchicalCategoryData(contentful, categoryIds);

        try {
          reviews = await powerReviewUtils.getCategoryReviews(
            categoryPath,
            evaConfiguration.organizationUnitId,
            formattedLocale
          );
        } catch (e) {
          console.error("[SSR] getServerSideProductListingPageData: getCategoryReviews failed", e);
        }
      } else if (slugSegments.length > 0) {
        for (let ii = 0; ii < slugSegments.length - 1; ++ii) {
          const slug = slugSegments.slice(0, ii + 1).join("/");
          const categoryBinding = await contentful.getCategoryBindingFromSlug(slug);

          if (categoryBinding) {
            assertEditorialType<Editorials.CategoryBinding>(categoryBinding, "categoryBinding");
            hierarchicalCategoryData.push({
              text: categoryBinding.fields.name,
              url: `/c/${categoryBinding.fields.slug}/`,
            });
          }
        }
      }

      if (categoryBindingSlugs) {
        for (slug of Object.keys(categoryBindingSlugs)) {
          categoryBindingSlugs[slug as keyof typeof categoryBindingSlugs] =
            "c/" + categoryBindingSlugs[slug as keyof typeof categoryBindingSlugs];
        }
      }

      return {
        ...data,
        reviews: reviews,
        page: page.fields,
        slugs: categoryBindingSlugs,
        breadcrumbs: hierarchicalCategoryData,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideProductListingPreviewPageData(
    context: GetPropsContext,
    slug = context.params?.slug,
    options: GetServerSideDataOptions = {}
  ) {
    return this.getServerSideProductListingPageData(context, true, slug, options);
  },

  async getServerSideSearchPageData(context: GetServerSidePropsContext) {
    // Generic data
    const contentful = new Contentful({ preview: false, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context);

    const pagePromise = contentful.getSearchPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    assertEditorialType<Editorials.SearchPage>(page, "searchPage");

    return {
      ...data,
      page,
    };
  },

  async getServerSideProductPageData(context: GetPropsContext, preview = false, productId: string) {
    // Generic data
    const formattedLocale = contentfulUtils.mapLocale(context.locale);
    const contentful = new Contentful({ preview, locale: formattedLocale });
    const evaConfiguration = await this.getEvaConfiguration(context);
    const dataPromise = this.getServerSideData(context, preview, {}, evaConfiguration);

    // Get actual product data
    const eva = new Eva({ organizationUnitId: evaConfiguration.organizationUnitId, locale: context.locale });

    const productResponse = await eva.getProductDetail(productId);
    const product = productResponse.Result;

    if (!product) {
      if (!CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_ADVANCED_PRODUCT_REDIRECTS)) {
        return null;
      }

      if (productResponse.Error?.Type !== "EntityNotFound") {
        console.warn(`Could not fetch product ${productId}: ${productResponse.Error}`);
        return null;
      }

      const admin = new EvaAdmin({ locale: context.locale });
      const productInDefaultOu = (await admin.getProductDetail(productId)).Result;

      if (!productInDefaultOu) {
        console.warn(`Could not find product ${productId} in default OU`);
        return null;
      }

      if (productInDefaultOu.product_next_id && typeof productInDefaultOu.product_next_id === "string") {
        const nextProduct = (await eva.getProductDetailByBackendId(productInDefaultOu.product_next_id)).Result;

        if (nextProduct?.product_id) {
          return {
            type: "redirect_next_product",
            data: { productId: nextProduct.product_id },
          } as const;
        }
      }

      if (
        productInDefaultOu.ecomm_online_category_master &&
        typeof productInDefaultOu.ecomm_online_category_master === "string"
      ) {
        const category = await contentful.getCategoryBindingFromId(productInDefaultOu.ecomm_online_category_master);

        if (category?.fields && typeof category.fields === "object" && "slug" in category.fields) {
          return {
            type: "redirect_category",
            data: { categorySlug: category.fields.slug as string },
          } as const;
        }
      }

      console.warn(`Product with ID ${productId} has no useful properties for a redirect`);
      return null;
    }

    const categorySegments = (product.ecomm_online_category_master as string)?.split("_") ?? [];
    const parentId =
      product.logical_level === "color"
        ? (product.logical_level_hierarchy as any).find((e: { name: string }) => e.name === "root")?.product_id
        : product.product_id;

    const productDetailWithChildren = evaUtils.getProductDetailWithChildren(eva, product, parentId);
    const categoryIds = categorySegments.map((_, idx, array) => array.slice(0, idx + 1).join("_"));
    const categoriesPromise = this.extractHierarchicalCategoryData(contentful, categoryIds);
    const pagesPromise = contentful.getProductDetailPageWithGeneric(parentId.toString());

    const [{ root, children, selected }, data, { page, genericPage }, categories] = await Promise.all([
      productDetailWithChildren,
      dataPromise,
      pagesPromise,
      categoriesPromise,
    ]);

    if (!root) {
      return null;
    }

    // get giftcard config if the product is of type "GiftCard"
    if (productUtils.isGiftCard(root)) {
      const giftConfig = await contentful.getVirtualGiftCardConfig();
      if (giftConfig) {
        assertEditorialType<Editorials.GiftCardsConfiguration>(giftConfig, "giftCardsConfiguration");
        giftConfig.fields.virtualCards = giftConfig.fields.virtualCards.filter(isActiveEntry);
        root.giftCardConfig = giftConfig;
      }
    }

    let reviews = null;
    try {
      reviews = await powerReviewUtils.getReviews(
        root.backend_id,
        evaConfiguration.organizationUnitId,
        formattedLocale
      );
    } catch (e) {
      console.error("[SSR] getServerSideProductPageData: getReviews failed", e);
    }

    assertEditorialType<Editorials.ProductDetailPage>(genericPage, "productDetailPage");

    if (page) {
      assertEditorialType<Editorials.ProductDetailPage>(page, "productDetailPage");
      const genericComponents = genericPage.fields.components ?? [];

      if (page.fields.seoMetadata) {
        genericPage.fields.seoMetadata = page.fields.seoMetadata;
      }
      // specific PDP can override disableTris regardless of generic value
      if (page.fields.disableTris !== undefined) {
        genericPage.fields.disableTris = page.fields.disableTris;
      }
      // elaborate specific-to-generic components replacement while filtering remaining ones to append
      const remainingComponents =
        page.fields.components?.filter((specificComponent) => {
          for (let i = 0; i < genericComponents.length; i++) {
            if (isSameEntry(specificComponent, genericComponents[i])) {
              genericComponents[i] = specificComponent;
              return false;
            }
          }
          return true;
        }) ?? [];

      // merge with generic ones if withoutGenericPdpComponents is false
      genericPage.fields.components = page.fields.withoutGenericPdpComponents
        ? page.fields.components ?? []
        : genericPage.fields.components?.concat(remainingComponents) ?? remainingComponents;
    }

    const typedRoot = root as CrossCountry.DetailedProduct;
    let typedChildren = children as CrossCountry.Product[];
    const typedSelected = selected as CrossCountry.DetailedProduct;

    // adding isCanonical attribute to the first children
    typedChildren = typedChildren.map((child, index) => ({ ...child, isCanonical: index == 0 }));
    return {
      ...data,
      root: typedRoot,
      reviews: reviews,
      children: typedChildren,
      selected: typedSelected,
      categories,
      page: genericPage.fields,
    };
  },

  async getServerSideProductPageDataBySku(context: GetPropsContext, preview = false, sku: string) {
    // Generic data
    const formattedLocale = contentfulUtils.mapLocale(context.locale);
    const contentful = new Contentful({ preview, locale: formattedLocale });
    const evaConfiguration = await this.getEvaConfiguration(context);
    const dataPromise = this.getServerSideData(context, preview, {}, evaConfiguration);

    // Get actual product data
    const eva = new Eva({ organizationUnitId: evaConfiguration.organizationUnitId, locale: context.locale });

    const productResponse = await eva.getProductDetailByBackendId(sku);
    const product = productResponse.Result;

    if (!product) {
      if (!CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_ADVANCED_PRODUCT_REDIRECTS)) {
        return null;
      }

      if (productResponse.Error?.Type !== "EntityNotFound") {
        console.warn(`Could not fetch product ${sku}: ${productResponse.Error}`);
        return null;
      }

      const admin = new EvaAdmin({ locale: context.locale });
      const productInDefaultOu = (await admin.getProductDetailByBackendId(sku)).Result;

      if (!productInDefaultOu) {
        console.warn(`Could not find product ${sku} in default OU`);
        return null;
      }

      if (productInDefaultOu.product_next_id) {
        return {
          type: "redirect_next_product",
          data: { productSku: productInDefaultOu.product_next_id },
        } as const;
      }

      if (
        productInDefaultOu.ecomm_online_category_master &&
        typeof productInDefaultOu.ecomm_online_category_master === "string"
      ) {
        const category = await contentful.getCategoryBindingFromId(productInDefaultOu.ecomm_online_category_master);

        if (category?.fields && typeof category.fields === "object" && "slug" in category.fields) {
          return {
            type: "redirect_category",
            data: { categorySlug: category.fields.slug as string },
          } as const;
        }
      }

      console.warn(`Product with ID ${sku} has no useful properties for a redirect`);
      return null;
    }

    const categorySegments = (product.ecomm_online_category_master as string)?.split("_") ?? [];
    const parentId =
      product.logical_level === "color"
        ? (product.logical_level_hierarchy as any)?.find((e: { name: string }) => e.name === "root")?.product_id
        : product.product_id;

    const productDetailWithChildren = evaUtils.getProductDetailWithChildren(eva, product, parentId);
    const categoryIds = categorySegments.map((_, idx, array) => array.slice(0, idx + 1).join("_"));
    const categoriesPromise = this.extractHierarchicalCategoryData(contentful, categoryIds);
    const pagesPromise = contentful.getProductDetailPageWithGeneric(parentId.toString());

    const [{ root, children, selected }, data, { page, genericPage }, categories] = await Promise.all([
      productDetailWithChildren,
      dataPromise,
      pagesPromise,
      categoriesPromise,
    ]);

    if (!root) {
      return null;
    }

    // get giftcard config if the product is of type "GiftCard"
    if (productUtils.isGiftCard(root)) {
      const giftConfig = await contentful.getVirtualGiftCardConfig();
      if (giftConfig) {
        assertEditorialType<Editorials.GiftCardsConfiguration>(giftConfig, "giftCardsConfiguration");
        giftConfig.fields.virtualCards = giftConfig.fields.virtualCards.filter(isActiveEntry);
        root.giftCardConfig = giftConfig;
      }
    }

    let reviews = null;
    try {
      reviews = await powerReviewUtils.getReviews(
        root.backend_id,
        evaConfiguration.organizationUnitId,
        formattedLocale
      );
    } catch (e) {
      console.error("[SSR] getServerSideProductPageDataBySku: getReviews failed", e);
    }

    assertEditorialType<Editorials.ProductDetailPage>(genericPage, "productDetailPage");

    if (page) {
      assertEditorialType<Editorials.ProductDetailPage>(page, "productDetailPage");
      const genericComponents = genericPage.fields.components ?? [];

      if (page.fields.seoMetadata) {
        genericPage.fields.seoMetadata = page.fields.seoMetadata;
      }
      // specific PDP can override disableTris regardless of generic value
      if (page.fields.disableTris !== undefined) {
        genericPage.fields.disableTris = page.fields.disableTris;
      }
      // elaborate specific-to-generic components replacement while filtering remaining ones to append
      const remainingComponents =
        page.fields.components?.filter((specificComponent) => {
          for (let i = 0; i < genericComponents.length; i++) {
            if (isSameEntry(specificComponent, genericComponents[i])) {
              genericComponents[i] = specificComponent;
              return false;
            }
          }
          return true;
        }) ?? [];

      // merge with generic ones if withoutGenericPdpComponents is false
      genericPage.fields.components = page.fields.withoutGenericPdpComponents
        ? page.fields.components ?? []
        : genericPage.fields.components?.concat(remainingComponents) ?? remainingComponents;
    }

    const typedRoot = root as CrossCountry.DetailedProduct;
    let typedChildren = children as CrossCountry.Product[];
    const typedSelected = selected as CrossCountry.DetailedProduct;

    // adding isCanonical attribute to the first children
    typedChildren = typedChildren.map((child, index) => ({ ...child, isCanonical: index == 0 }));

    return {
      ...data,
      root: typedRoot,
      reviews: reviews,
      children: typedChildren,
      selected: typedSelected,
      categories,
      page: genericPage.fields,
    };
  },

  async getServerSideLoginSignUpPageData(
    context: GetServerSidePropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getLoginSignUpPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.SpecialPage>(page, "specialPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideCartPageData(context: GetPropsContext, preview = false, options: GetServerSideDataOptions = {}) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getCartPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.CartPage>(page, "cartPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideCheckoutPageData(
    context: GetPropsContext,
    preview = false,
    options: GetServerSideDataOptions = {}
  ) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.getCheckoutPage();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.CheckoutPage>(page, "checkoutPage");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSide404PageData(context: GetPropsContext, preview = false, options: GetServerSideDataOptions = {}) {
    // Generic data
    const contentful = new Contentful({ preview, locale: contentfulUtils.mapLocale(context.locale) });
    const dataPromise = this.getServerSideData(context, preview, options);

    // Editorial page data
    const pagePromise = contentful.get404Page();

    const [data, page] = await Promise.all([dataPromise, pagePromise]);

    if (page) {
      assertEditorialType<Editorials.Page404>(page, "page404");
      return {
        ...data,
        page: page.fields,
      };
    }

    return {
      ...data,
      page: null,
    };
  },

  async getServerSideEntityTranslations(context: GetPropsContext, entityType: string | undefined) {
    const lang = context?.locale?.split("-")[0];
    const admin = new EvaAdmin({ locale: context.locale });
    const translations = await admin.listEntityTranslations({
      PageConfig: {
        Filter: {
          EntityType: entityType,
          LanguageID: lang || "en",
        },
      },
    });
    return translations;
  },

  async getRedirects(locale?: string) {
    if (!locale) {
      return;
    }

    const contentful = new Contentful({ locale: contentfulUtils.mapLocale(locale) });

    const redirectFile = await contentful.getRedirectFile(locale);

    if (redirectFile) {
      if (redirectFile.fields.file.contentType.split("/")[1] !== "csv") {
        Logger.instance.error("The file is not of type CSV");
        return;
      }

      const csvContent = await fetch(`https:${redirectFile.fields.file.url}`)
        .then((r) => r.text())
        .catch((error) => {
          Logger.instance.error("Failed to fetch CSV", error);
          return;
        });

      if (!csvContent?.length) {
        Logger.instance.error("CSV empty, skipping");
        return;
      }

      const parseCsvContent = (csvText: string) => {
        const lines = csvText.trim().split("\n");
        const result: Record<string, string> = {};
        lines.slice(1).forEach((line) => {
          let [fromUrl, toUrl] = line.split(",");

          if (!toUrl) {
            [fromUrl, toUrl] = line.split(";");
          }

          const trimmedFromUrl = fromUrl.trim();
          const normalizedFromUrl = trimmedFromUrl.endsWith("/") ? trimmedFromUrl : trimmedFromUrl + "/";
          const trimmedToUrl = toUrl?.trim();
          const normalizedToUrl = trimmedToUrl?.endsWith("/") ? trimmedToUrl : trimmedToUrl + "/";

          result[normalizedFromUrl] = normalizedToUrl;
        });
        return result;
      };

      const jsonContent = parseCsvContent(csvContent);

      if (Object.keys(jsonContent).length === 0) {
        Logger.instance.error("JSON empty, skipping");
        return;
      }

      return jsonContent;
    }
  },

  getHost() {
    let host = "https://www.kikocosmetics.com";

    switch (process.env.VERCEL_ENV) {
      case "production":
        host = "https://www.kikocosmetics.com";
        break;
      case "preview":
        host = `https://${process.env.VERCEL_URL}`;
        break;
      case "development":
        host = "http://localhost:3000";
        break;
    }
    return host;
  },
};

export default ssrUtils;
